'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { ComponentProps, PropsWithChildren, ReactNode, useId } from 'react';
import ActionWorldCardBadgeList from '@/components/ActionWorldCardBadgeList';
import ActionWorldCardHeader from '@/components/ActionWorldCardHeader';
import ActionWorldCardImage from '@/components/ActionWorldCardImage';
import ActionWorldCardRankingBadge from '@/components/ActionWorldCardRankingBadge';
import ViverseCard from '@/components/ViverseCard';
import ViverseSkeleton from '@/components/ViverseSkeleton';
import { useCardInteraction } from '@/hooks/useCardInteraction';
import { useDwellPlayback } from '@/hooks/useDwellPlayback';
import { useLikedRoom } from '@/hooks/useLikedRoom';
import { hasVerifiedCreatorBadge } from '@/lib/cms';
import { cn } from '@/lib/utils';
import { isNewRoom, isUpdatedRoom } from '@/lib/viverse-world-card';
import roomDefaultImage from '@/public/images/room-default.png';
import type { WorldCardInfo } from '@/types/viverse-world-card';
type ActionWorldCardProps = PropsWithChildren<ComponentProps<typeof ViverseCard> & {
  info: WorldCardInfo;
  anchorId?: string;
  href?: string;
  showMoreAction?: boolean;
  moreActionContent?: ReactNode;
  isLive?: boolean;
  liveCount?: number;
  customDesc?: string;
  hideLiked?: boolean;
  shareOnly?: boolean;
  className?: string;
  imageClassName?: string;
  isCoOwnedRoom?: boolean;
  onAfterMutatingWorld?: (deleteRoomId?: string) => void;
  stopSetAsFeatured?: boolean;
  hidePremiumBadge?: boolean;
  ranking?: boolean;
  rankingIndex?: number;
  cardOnly?: boolean;
  isNew?: boolean;
  isUpdated?: boolean;
  isMission?: boolean;
  isExclusive?: boolean;
  isSelected?: boolean;
  isHot?: boolean;
  isTop?: boolean;
  forceShowPremiumBadge?: boolean;
  disableAutoPreview?: boolean;
  noAccess?: boolean;
}>;
export default function ActionWorldCard(t0) {
  const $ = _c(101);
  let anchorId;
  let className;
  let customDesc;
  let href;
  let imageClassName;
  let info;
  let isExclusive;
  let isHot;
  let isNew;
  let isSelected;
  let isTop;
  let isUpdated;
  let moreActionContent;
  let noAccess;
  let onAfterMutatingWorld;
  let props;
  let rankingIndex;
  let t1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== t0) {
    ({
      href,
      info,
      anchorId,
      showMoreAction: t1,
      moreActionContent,
      customDesc,
      hideLiked: t2,
      shareOnly: t3,
      isCoOwnedRoom: t4,
      className,
      imageClassName,
      onAfterMutatingWorld,
      stopSetAsFeatured: t5,
      hidePremiumBadge: t6,
      ranking: t7,
      rankingIndex,
      cardOnly: t8,
      isNew,
      isUpdated,
      isMission: t9,
      isExclusive,
      isSelected,
      isHot,
      isTop,
      forceShowPremiumBadge: t10,
      disableAutoPreview: t11,
      noAccess,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = anchorId;
    $[2] = className;
    $[3] = customDesc;
    $[4] = href;
    $[5] = imageClassName;
    $[6] = info;
    $[7] = isExclusive;
    $[8] = isHot;
    $[9] = isNew;
    $[10] = isSelected;
    $[11] = isTop;
    $[12] = isUpdated;
    $[13] = moreActionContent;
    $[14] = noAccess;
    $[15] = onAfterMutatingWorld;
    $[16] = props;
    $[17] = rankingIndex;
    $[18] = t1;
    $[19] = t10;
    $[20] = t11;
    $[21] = t2;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    anchorId = $[1];
    className = $[2];
    customDesc = $[3];
    href = $[4];
    imageClassName = $[5];
    info = $[6];
    isExclusive = $[7];
    isHot = $[8];
    isNew = $[9];
    isSelected = $[10];
    isTop = $[11];
    isUpdated = $[12];
    moreActionContent = $[13];
    noAccess = $[14];
    onAfterMutatingWorld = $[15];
    props = $[16];
    rankingIndex = $[17];
    t1 = $[18];
    t10 = $[19];
    t11 = $[20];
    t2 = $[21];
    t3 = $[22];
    t4 = $[23];
    t5 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  const showMoreAction = t1 === undefined ? false : t1;
  const hideLiked = t2 === undefined ? false : t2;
  const shareOnly = t3 === undefined ? false : t3;
  const isCoOwnedRoom = t4 === undefined ? false : t4;
  const stopSetAsFeatured = t5 === undefined ? false : t5;
  const hidePremiumBadge = t6 === undefined ? true : t6;
  const ranking = t7 === undefined ? false : t7;
  const cardOnly = t8 === undefined ? false : t8;
  const isMission = t9 === undefined ? false : t9;
  const forceShowPremiumBadge = t10 === undefined ? false : t10;
  const disableAutoPreview = t11 === undefined ? false : t11;
  const {
    id,
    title,
    image: t12,
    view_count,
    like_count,
    hubs_url,
    owner,
    is_liked,
    is_live,
    external_link,
    is_voucher_room,
    vod_duration,
    vod_last_update_date,
    trailer_videos
  } = info;
  const image = t12 === undefined ? roomDefaultImage.src : t12;
  const resolvedImage = noAccess ? roomDefaultImage.src : image;
  const showNew = isNew ?? isNewRoom(info.first_public_date);
  const showUpdated = isUpdated ?? isUpdatedRoom(info.last_updated_at, info.first_public_date);
  const showExclusive = isExclusive ?? info.is_exclusive ?? false;
  const showSelected = isSelected ?? info.is_selected ?? false;
  const showHot = isHot ?? info.is_hot ?? false;
  const showTop = isTop ?? info.is_top ?? false;
  const {
    isLiked,
    handleClickLike
  } = useLikedRoom(id || "", href || hubs_url || "/", is_liked);
  const showPremiumTag = forceShowPremiumBadge || !hidePremiumBadge && is_voucher_room;
  const isVOD = typeof vod_duration === "number" && !!vod_last_update_date;
  const fallbackId = useId();
  const uniqueId = id || fallbackId;
  const vodId = info.vod_id || trailer_videos?.[0]?.vod_id;
  const {
    isHovered,
    onPointerEnter,
    onPointerLeave,
    onContextMenu,
    onTouchStart,
    onTouchEnd,
    onTouchCancel
  } = useCardInteraction(uniqueId);
  const t13 = disableAutoPreview ? undefined : vodId;
  let t14;
  if ($[29] !== t13) {
    t14 = {
      vodId: t13
    };
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const {
    ref: dwellRef,
    isPlaying: isDwellPlaying
  } = useDwellPlayback(t14);
  const T0 = ViverseCard;
  const t15 = "world-card";
  const t16 = anchorId ?? id;
  const t17 = cn("group @container rounded-[25px] transition-all duration-300 select-none lg:overflow-hidden lg:p-2 lg:hover:bg-[#596677]/30", className, {
    relative: ranking || isMission || showNew || showUpdated || showExclusive || showSelected || showHot || showTop
  });
  let t18;
  if ($[31] !== ranking || $[32] !== rankingIndex) {
    t18 = ranking && <ActionWorldCardRankingBadge index={rankingIndex} />;
    $[31] = ranking;
    $[32] = rankingIndex;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const t19 = info.content_type?.code;
  let t20;
  if ($[34] !== isMission || $[35] !== showExclusive || $[36] !== showHot || $[37] !== showNew || $[38] !== showSelected || $[39] !== showTop || $[40] !== showUpdated || $[41] !== t19) {
    t20 = <ActionWorldCardBadgeList isMission={isMission} isNew={showNew} isUpdated={showUpdated} isExclusive={showExclusive} isSelected={showSelected} isHot={showHot} isTop={showTop} cardType={t19} />;
    $[34] = isMission;
    $[35] = showExclusive;
    $[36] = showHot;
    $[37] = showNew;
    $[38] = showSelected;
    $[39] = showTop;
    $[40] = showUpdated;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== info.preferred_devices || $[44] !== isVOD) {
    t21 = isVOD ? [] : info.preferred_devices;
    $[43] = info.preferred_devices;
    $[44] = isVOD;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = owner?.display_name;
  let t23;
  if ($[46] !== external_link || $[47] !== href || $[48] !== hubs_url || $[49] !== imageClassName || $[50] !== isDwellPlaying || $[51] !== isHovered || $[52] !== is_live || $[53] !== resolvedImage || $[54] !== showPremiumTag || $[55] !== t21 || $[56] !== t22 || $[57] !== title || $[58] !== vodId || $[59] !== vod_duration) {
    t23 = <ActionWorldCardImage href={href} externalLink={external_link} hubsUrl={hubs_url} image={resolvedImage} isLive={is_live} imageClassName={imageClassName} preferredDevices={t21} showPremiumTag={showPremiumTag} vodDuration={vod_duration} vodId={vodId} isCardHovered={isHovered} isDwellPlaying={isDwellPlaying} title={title} ownerDisplayName={t22} />;
    $[46] = external_link;
    $[47] = href;
    $[48] = hubs_url;
    $[49] = imageClassName;
    $[50] = isDwellPlaying;
    $[51] = isHovered;
    $[52] = is_live;
    $[53] = resolvedImage;
    $[54] = showPremiumTag;
    $[55] = t21;
    $[56] = t22;
    $[57] = title;
    $[58] = vodId;
    $[59] = vod_duration;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  let t24;
  if ($[61] !== cardOnly || $[62] !== customDesc || $[63] !== external_link || $[64] !== handleClickLike || $[65] !== hideLiked || $[66] !== href || $[67] !== hubs_url || $[68] !== id || $[69] !== info || $[70] !== isCoOwnedRoom || $[71] !== isLiked || $[72] !== isVOD || $[73] !== like_count || $[74] !== moreActionContent || $[75] !== noAccess || $[76] !== onAfterMutatingWorld || $[77] !== owner || $[78] !== shareOnly || $[79] !== showMoreAction || $[80] !== stopSetAsFeatured || $[81] !== title || $[82] !== view_count || $[83] !== vod_last_update_date) {
    t24 = !cardOnly && <ActionWorldCardHeader id={id} title={title} href={href} externalLink={external_link} hubsUrl={hubs_url} info={info} showMoreAction={noAccess ? false : showMoreAction} moreActionContent={moreActionContent} shareOnly={shareOnly} isCoOwnedRoom={isCoOwnedRoom} onAfterMutatingWorld={onAfterMutatingWorld} stopSetAsFeatured={stopSetAsFeatured} customDesc={customDesc} ownerDisplayName={owner?.display_name} ownerId={owner?.community_user_id} ownerIsVerified={hasVerifiedCreatorBadge(owner)} viewCount={view_count} likeCount={like_count} hideLiked={noAccess ? true : hideLiked} isLiked={isLiked} isVOD={isVOD} vodLastUpdateDate={vod_last_update_date} noAccess={noAccess} handleClickLike={handleClickLike} />;
    $[61] = cardOnly;
    $[62] = customDesc;
    $[63] = external_link;
    $[64] = handleClickLike;
    $[65] = hideLiked;
    $[66] = href;
    $[67] = hubs_url;
    $[68] = id;
    $[69] = info;
    $[70] = isCoOwnedRoom;
    $[71] = isLiked;
    $[72] = isVOD;
    $[73] = like_count;
    $[74] = moreActionContent;
    $[75] = noAccess;
    $[76] = onAfterMutatingWorld;
    $[77] = owner;
    $[78] = shareOnly;
    $[79] = showMoreAction;
    $[80] = stopSetAsFeatured;
    $[81] = title;
    $[82] = view_count;
    $[83] = vod_last_update_date;
    $[84] = t24;
  } else {
    t24 = $[84];
  }
  let t25;
  if ($[85] !== T0 || $[86] !== dwellRef || $[87] !== onContextMenu || $[88] !== onPointerEnter || $[89] !== onPointerLeave || $[90] !== onTouchCancel || $[91] !== onTouchEnd || $[92] !== onTouchStart || $[93] !== props || $[94] !== t16 || $[95] !== t17 || $[96] !== t18 || $[97] !== t20 || $[98] !== t23 || $[99] !== t24) {
    t25 = <T0 {...props} data-component={t15} data-room-id={t16} ref={dwellRef} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} onContextMenu={onContextMenu} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={onTouchCancel} className={t17}>{t18}{t20}{t23}{t24}</T0>;
    $[85] = T0;
    $[86] = dwellRef;
    $[87] = onContextMenu;
    $[88] = onPointerEnter;
    $[89] = onPointerLeave;
    $[90] = onTouchCancel;
    $[91] = onTouchEnd;
    $[92] = onTouchStart;
    $[93] = props;
    $[94] = t16;
    $[95] = t17;
    $[96] = t18;
    $[97] = t20;
    $[98] = t23;
    $[99] = t24;
    $[100] = t25;
  } else {
    t25 = $[100];
  }
  return t25;
}
export function ActionWorldCardSkeleton(t0) {
  const $ = _c(9);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("text-card-foreground cursor-wait rounded-lg border-0 bg-transparent shadow-xs lg:p-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    t3 = {
      opacity: 1
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ViverseSkeleton className="aspect-[1.72] w-full rounded-[25px]" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ViverseSkeleton className="h-4 w-40 rounded-lg max-lg:w-3/4" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="flex w-full flex-col gap-2">{t4}<div className="flex w-full flex-col gap-2">{t5}<div className="flex gap-2"><ViverseSkeleton className="h-4 w-12 rounded-lg" /><ViverseSkeleton className="size-4 rounded-lg" /><ViverseSkeleton className="h-4 w-20 rounded-lg max-lg:block lg:hidden" /></div><ViverseSkeleton className="h-4 w-20 rounded-lg max-lg:hidden" /></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t1) {
    t7 = <motion.div className={t1} initial={t2} animate={t3}>{t6}</motion.div>;
    $[7] = t1;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}